/**
 * MemoryFixture — a frozen, content-addressed, portable corpus of memories.
 *
 * It is the memory analog of a scenario's fixed seed: the pinned "WHICH
 * memories exist" input for a **SEEDED** benchmark arm (`RunProfile.memoryMode:
 * 'SEEDED'` — see `run-profile.ts`, and §9 of the orchestration-benchmark-
 * harness design). A SEEDED run injects exactly these rows every trial, so a
 * memory comparison varies only the corpus and never run-to-run store drift —
 * the deterministic instrument OFF/LIVE cannot provide (OFF injects nothing;
 * LIVE lets the store change under you).
 *
 * Three deliberate properties:
 *  1. **Content-addressed** — `fixtureHash` is a stable hash of the rows, so two
 *     runs with the same hash are provably corpus-identical (the memory analog
 *     of the sim's provenance triple). Recorded on `BenchmarkRun.resolvedProfile`
 *     via the inline `RunProfile.memoryFixture`, so an arm's corpus is auditable
 *     without a separate store.
 *  2. **Bindingless + lifecycleless** — pure content (`content`/`type`/
 *     `priority`), no `orgId`, no `AgentMemoryBinding`, no `status`. That is what
 *     lets the host inject it identically whether or not the org holds those
 *     rows, and makes it a plain portable JSON artifact.
 *  3. **Portable** — a JSON file, so a non-cortex stack (code-mode/manual) can
 *     load the SAME fixture into its own prompt for a fair cross-stack memory
 *     comparison.
 *
 * NOT here (by design): a `provider` axis (naive vs scored vs KG). The scored
 * and KG retrievers do not exist yet (memory-v4 Phases C/D), so per the
 * `.strict()` RunProfile rule — only knobs with a real consuming mechanism are
 * representable — there is no provider field. SEEDED renders through the
 * existing naive `renderAgentMemoryBlock`.
 */
import { z } from 'zod';
/** One row of a fixture — plain memory content, no binding/lifecycle. */
export const MemoryFixtureRowSchema = z.object({
    content: z.string().min(1).max(4000),
    type: z.enum(['RULE', 'CONTEXT', 'PREFERENCE']),
    /** Injection priority (0–2), mirrors `AgentMemory.priority`; drives the naive cap-15 ordering. */
    priority: z.number().int().min(0).max(2).default(0),
});
/**
 * cyrb53 — a fast, well-distributed non-cryptographic 53-bit hash. Pure JS (no
 * `node:crypto`) so `@cortex/types` stays isomorphic/browser-safe. This is an
 * IDENTITY hash ("are these two fixtures the same corpus"), never security.
 */
function cyrb53(str, seed = 0) {
    let h1 = 0xdeadbeef ^ seed;
    let h2 = 0x41c6ce57 ^ seed;
    for (let i = 0; i < str.length; i++) {
        const ch = str.charCodeAt(i);
        h1 = Math.imul(h1 ^ ch, 2654435761);
        h2 = Math.imul(h2 ^ ch, 1597334677);
    }
    h1 = Math.imul(h1 ^ (h1 >>> 16), 2246822507);
    h1 ^= Math.imul(h2 ^ (h2 >>> 13), 3266489909);
    h2 = Math.imul(h2 ^ (h2 >>> 16), 2246822507);
    h2 ^= Math.imul(h1 ^ (h1 >>> 13), 3266489909);
    const n = 4294967296 * (2097151 & h2) + (h1 >>> 0);
    return n.toString(16).padStart(14, '0');
}
/**
 * The corpus identity: a hash of the canonical, ORDER-INDEPENDENT serialization
 * of the rows — each row is `JSON.stringify([type, priority, content])`, so
 * content may hold ANY character (quotes, spaces, newlines, control bytes) with
 * no delimiter collision (rows can't merge or split); the per-row JSON strings
 * are sorted and newline-joined, so row ORDER doesn't matter. `mf1_`-versioned.
 * Accepts raw rows (priority may be undefined → treated as 0, matching the
 * schema default) so callers can hash before `.parse()`.
 */
export function computeMemoryFixtureHash(rows) {
    const canonical = rows
        .map((r) => JSON.stringify([r.type, r.priority ?? 0, r.content]))
        .sort()
        .join('\n');
    return `mf1_${cyrb53(canonical)}`;
}
/**
 * A frozen memory corpus. `fixtureHash` MUST equal `computeMemoryFixtureHash`
 * of the rows — enforced here so a hand-edited fixture that forgot to rehash is
 * rejected at the boundary (a benchmark arm must not run a corpus that differs
 * from the hash it records). Use {@link buildMemoryFixture} to construct one
 * with a correct hash.
 */
export const MemoryFixtureSchema = z
    .object({
    name: z.string().min(1).max(200),
    /** Bumps when the rows change — the corpus's own version, distinct from the run. */
    version: z.number().int().nonnegative().default(1),
    /** cyrb53 of the canonical rows (see computeMemoryFixtureHash). */
    fixtureHash: z.string().min(1),
    rows: z.array(MemoryFixtureRowSchema).min(1).max(500),
    /** Free-text: how this corpus was built ("export of avi-polb approved @2026-08-04", "poisoned"). */
    provenance: z.string().max(2000).optional(),
    /** Pin the fixture to the scenario it was curated against (fixture ⇄ (scenario,version)). */
    curatedAgainst: z
        .object({ scenario: z.string().min(1), version: z.number().int().nonnegative() })
        .optional(),
})
    .strict()
    .superRefine((f, ctx) => {
    const expected = computeMemoryFixtureHash(f.rows);
    if (f.fixtureHash !== expected) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['fixtureHash'],
            message: `fixtureHash "${f.fixtureHash}" does not match the rows' content hash "${expected}" — ` +
                `the fixture was altered without rehashing (use buildMemoryFixture).`,
        });
    }
});
/**
 * Construct a valid {@link MemoryFixture} with a correct `fixtureHash` from raw
 * rows — the one-liner an export/curate tool (or a test) uses so it never
 * hand-computes the hash.
 */
export function buildMemoryFixture(input) {
    const rows = input.rows.map((r) => ({ ...r, priority: r.priority ?? 0 }));
    return MemoryFixtureSchema.parse({
        ...input,
        rows,
        fixtureHash: computeMemoryFixtureHash(rows),
    });
}
//# sourceMappingURL=memory-fixture.js.map